import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, MapPin, Plus, X, Users, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

export default function CalendarPage() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [plans, setPlans] = useState([]);
  const [missions, setMissions] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [scope, setScope] = useState({ kind: "me", id: user?.id }); // me | user | store
  const [period, setPeriod] = useState("week"); // week | month
  const [offset, setOffset] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [newEv, setNewEv] = useState({ title: "", date: "", time: "", duration_min: 60, location: "", note: "", color: "#6366f1" });

  const loadStatic = async () => {
    const [{ data: u }, { data: s }] = await Promise.all([api.get("/users"), api.get("/stores")]);
    setUsers(u); setStores(s);
  };
  useEffect(() => { loadStatic(); }, []);

  const load = async () => {
    const scopeUser = scope.kind === "me" ? user?.id : scope.kind === "user" ? scope.id : null;
    const params = scope.kind === "store" ? {} : { user_id: scopeUser };
    const [{ data: a }, { data: p }, { data: m }, { data: ev }] = await Promise.all([
      api.get("/activities"), api.get("/plans"), api.get("/missions"),
      api.get("/calendar/events", { params }),
    ]);
    // Filter by scope
    const filterFn = (row, keyStore, keyUser) => {
      if (scope.kind === "me") return row[keyUser] === user?.id || row[keyUser] === user?.id;
      if (scope.kind === "user") return row[keyUser] === scope.id;
      if (scope.kind === "store") return row[keyStore] === scope.id;
      return true;
    };
    setActivities(a.filter(x => scope.kind === "store" ? x.store_id === scope.id
                                  : scope.kind === "user" ? x.user_id === scope.id
                                  : true));
    setPlans(p.filter(x => scope.kind === "user" ? x.responsible_id === scope.id : scope.kind === "store" ? x.store_id === scope.id : true));
    setMissions(m.filter(x => scope.kind === "user" ? (x.assignee_id === scope.id || x.assigner_id === scope.id) : true));
    setEvents(ev);
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [scope, user]);

  const now = new Date();
  const startOfWeek = useMemo(() => {
    const d = new Date(now); const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1) + offset * 7);
    d.setHours(0, 0, 0, 0); return d;
  }, [offset]);
  const startOfMonth = useMemo(() => {
    const d = new Date(now); d.setMonth(d.getMonth() + offset); d.setDate(1); d.setHours(0,0,0,0); return d;
  }, [offset]);

  const days = useMemo(() => {
    if (period === "week") return [...Array(7)].map((_, i) => { const d = new Date(startOfWeek); d.setDate(startOfWeek.getDate() + i); return d; });
    const daysInMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0).getDate();
    const firstDay = (startOfMonth.getDay() + 6) % 7;
    const arr = [];
    for (let i = 0; i < firstDay; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), d));
    while (arr.length % 7) arr.push(null);
    return arr;
  }, [period, startOfWeek, startOfMonth]);

  const today = new Date().toDateString();

  const eventsForDay = (d) => {
    if (!d) return [];
    const iso = d.toISOString().slice(0, 10);
    const list = [];
    activities.filter(a => a.date === iso).forEach(a => list.push({ type: "visit", label: a.visit_type, sub: a.description?.slice(0, 30) }));
    activities.filter(a => a.next_visit_date === iso).forEach(a => list.push({ type: "next", label: "Prochaine visite", sub: a.visit_type }));
    plans.filter(p => p.deadline === iso).forEach(p => list.push({ type: "deadline", label: `Échéance : ${p.objective}` }));
    missions.filter(m => m.deadline === iso).forEach(m => list.push({ type: "mission", label: `Mission : ${m.title}` }));
    events.filter(ev => ev.date === iso).forEach(ev => list.push({ type: "personal", label: ev.title, sub: ev.time || ev.location, id: ev.id, color: ev.color }));
    return list;
  };

  const typeStyle = {
    visit: { c: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-100" },
    next: { c: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    deadline: { c: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-100" },
    mission: { c: "bg-purple-500", chip: "bg-purple-50 text-purple-700 border-purple-100" },
    personal: { c: "bg-indigo-500", chip: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    if (!newEv.title || !newEv.date) return;
    try {
      await api.post("/calendar/events", { ...newEv, duration_min: Number(newEv.duration_min) });
      toast.success("Événement ajouté"); setAddOpen(false);
      setNewEv({ title: "", date: "", time: "", duration_min: 60, location: "", note: "", color: "#6366f1" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delEvent = async (id) => {
    if (!confirm("Supprimer cet événement ?")) return;
    try { await api.delete(`/calendar/events/${id}`); load(); } catch {}
  };

  const label = period === "week"
    ? `Semaine du ${startOfWeek.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`
    : startOfMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const scopeLabel = scope.kind === "me" ? "Mon calendrier"
                   : scope.kind === "user" ? users.find(u => u.id === scope.id)?.name || "—"
                   : scope.kind === "store" ? stores.find(s => s.id === scope.id)?.name || "—" : "—";

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Calendrier</h1>
          <p className="text-slate-500 mt-1">Visites, missions, échéances et vos événements personnels.</p>
        </div>
        <button data-testid="event-new" onClick={() => { setAddOpen(true); setNewEv({...newEv, date: new Date().toISOString().slice(0,10)}); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
          <Plus className="w-4 h-4"/> Ajouter un événement
        </button>
      </div>

      {/* Scope + Period */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5"/> Calendrier de :</div>
        <SearchSelect value={scope.kind === "me" ? "me" : scope.kind + ":" + scope.id} testid="cal-scope" size="sm" minWidth={220} className="max-w-[280px]" menuWidth={300}
                      onChange={v => {
                        if (!v || v === "me") setScope({ kind: "me", id: user?.id });
                        else { const [k, id] = v.split(":"); setScope({ kind: k, id }); }
                      }}
                      options={[
                        { value: "me", label: `Moi (${user?.name})` },
                        ...users.filter(u => u.role === "director").map(u => ({ value: `user:${u.id}`, label: `${u.name} · DR` })),
                        ...users.filter(u => u.role === "manager").map(u => ({ value: `user:${u.id}`, label: `${u.name} · BM` })),
                        ...users.filter(u => ["rt","mm"].includes(u.role)).map(u => ({ value: `user:${u.id}`, label: `${u.name} · ${u.role.toUpperCase()}` })),
                        ...stores.map(s => ({ value: `store:${s.id}`, label: `${s.name} · Corner` })),
                      ]}/>
        <span className="text-xs text-slate-400">·</span>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {[["week","Semaine"],["month","Mois"]].map(([v,l]) => (
            <button key={v} onClick={() => { setPeriod(v); setOffset(0); }} data-testid={`cal-period-${v}`}
                    className={`px-3 py-1.5 text-xs font-semibold ${period === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="ml-auto text-xs font-semibold text-slate-700">{scopeLabel}</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => setOffset(offset - 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4"/></button>
            <button data-testid="cal-next" onClick={() => setOffset(offset + 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4"/></button>
            <button data-testid="cal-today" onClick={() => setOffset(0)} className="ml-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium">Aujourd'hui</button>
          </div>
          <div className="font-display text-lg font-semibold text-slate-800 capitalize">{label}</div>
        </div>

        <div className={`mt-6 grid gap-2 ${period === "week" ? "grid-cols-1 md:grid-cols-7" : "grid-cols-7"}`}>
          {period === "month" && ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
            <div key={d} className="text-center text-[10px] font-bold text-slate-400 uppercase pb-1">{d}</div>
          ))}
          {days.map((d, i) => {
            if (!d) return <div key={i}/>;
            const isToday = d.toDateString() === today;
            const evs = eventsForDay(d);
            return (
              <div key={i} className={`rounded-xl border p-2 sm:p-3 ${period === "week" ? "min-h-[180px]" : "min-h-[110px]"} ${isToday ? "bg-blue-600 text-white border-blue-600" : "bg-slate-50 border-slate-100"}`}>
                <div className={`text-xs font-semibold ${isToday ? "text-white" : "text-slate-500"}`}>
                  {period === "week" ? d.toLocaleDateString("fr-FR", { weekday: "short" }) + ". " + d.getDate() : d.getDate()}
                </div>
                <div className="mt-2 space-y-1">
                  {evs.length === 0 && <div className={`text-[10px] italic ${isToday ? "text-blue-100" : "text-slate-400"}`}>—</div>}
                  {evs.slice(0, period === "month" ? 3 : 8).map((e, j) => (
                    <div key={j} className={`group rounded-lg px-2 py-1 border text-[11px] ${isToday ? "bg-white/10 border-white/20 text-white" : typeStyle[e.type]?.chip}`}
                         style={e.type === "personal" && e.color && !isToday ? { background: `${e.color}18`, color: e.color, borderColor: `${e.color}44` } : {}}>
                      <div className="flex items-center gap-1.5 font-semibold">
                        <span className={`w-1.5 h-1.5 rounded-full ${typeStyle[e.type]?.c}`}/>
                        <span className="truncate flex-1">{e.label}</span>
                        {e.type === "personal" && e.id && (
                          <button onClick={() => delEvent(e.id)} data-testid={`event-del-${e.id}`}
                                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500"><X className="w-2.5 h-2.5"/></button>
                        )}
                      </div>
                      {e.sub && period === "week" && <div className={`${isToday ? "text-blue-100" : "text-slate-500"} truncate`}>{e.sub}</div>}
                    </div>
                  ))}
                  {evs.length > (period === "month" ? 3 : 8) && (
                    <div className={`text-[9px] ${isToday ? "text-blue-100" : "text-slate-400"}`}>+{evs.length - (period === "month" ? 3 : 8)} autre(s)</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAddOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={submitEvent} className="bg-white rounded-2xl w-full max-w-md border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-lg font-semibold">Nouvel événement</h2>
              <button type="button" onClick={() => setAddOpen(false)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-3">
              <input required placeholder="Titre (ex: RDV médecin, formation externe)"
                     value={newEv.title} onChange={e => setNewEv({...newEv, title: e.target.value})} data-testid="event-title"
                     className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <div className="grid grid-cols-3 gap-2">
                <input type="date" required value={newEv.date} onChange={e => setNewEv({...newEv, date: e.target.value})} data-testid="event-date"
                       className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
                <input type="time" value={newEv.time} onChange={e => setNewEv({...newEv, time: e.target.value})} data-testid="event-time"
                       placeholder="HH:MM" className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
                <input type="number" min={5} value={newEv.duration_min} onChange={e => setNewEv({...newEv, duration_min: e.target.value})} data-testid="event-duration"
                       placeholder="Durée min" className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <input placeholder="Lieu (optionnel)" value={newEv.location} onChange={e => setNewEv({...newEv, location: e.target.value})}
                     className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <textarea rows={2} placeholder="Note (optionnel)" value={newEv.note} onChange={e => setNewEv({...newEv, note: e.target.value})}
                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-none"/>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Couleur</span>
                {["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#0ea5e9"].map(c => (
                  <button key={c} type="button" onClick={() => setNewEv({...newEv, color: c})}
                          className={`w-6 h-6 rounded-full ${newEv.color === c ? "ring-2 ring-offset-2 ring-slate-400" : ""}`}
                          style={{ background: c }}/>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setAddOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="event-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">Ajouter</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
